import type { CampaignSource, IPrizePopCampaign, PrizePopIntervalTimeRange } from '../../../../../types/campaign.types';

// Parse UTC time string and apply time components to baseDate
// Supports two formats:
// 1. Full ISO: "2026-03-11T17:18:00Z" - extracts time from full datetime
// 2. Time-only: "17:18:00Z" - parses time directly
// Both extract UTC time and apply it to baseDate's day for repeating slots across date range
const parseSlotTime = (timeStr: string, baseDate: Date): Date => {
  let hour: number, min: number, sec: number;

  if (timeStr.includes('T')) {
    // Full ISO format: "2026-03-11T17:18:00Z"
    const parsed = new Date(timeStr);
    hour = parsed.getUTCHours();
    min = parsed.getUTCMinutes();
    sec = parsed.getUTCSeconds();
  } else {
    // Time-only format: "17:18:00Z" or "17:18:00"
    const timeOnly = timeStr.replace('Z', '');
    [hour, min, sec] = timeOnly.split(':').map(Number);
  }

  return new Date(
    Date.UTC(
      baseDate.getUTCFullYear(),
      baseDate.getUTCMonth(),
      baseDate.getUTCDate(),
      hour,
      min,
      sec,
      0,
    ),
  );
};

// Check if current time is inside a specific hour slot for a given day (baseDate)
const isNowInSlot = (now: Date, startTime: string, endTime: string, baseDate: Date): boolean => {
  const slotStart = parseSlotTime(startTime, baseDate);
  const slotEnd = parseSlotTime(endTime, baseDate);
  return now >= slotStart && now <= slotEnd;
};

// Check if any slot in hoursRange is currently active
// Iterates through each day in the range and checks if now falls within any slot
const checkIfSlotIsActive = (now: Date, hoursRange: PrizePopIntervalTimeRange[], rangeStart: Date, rangeEnd: Date) => {
  let currentDay = new Date(
    Date.UTC(rangeStart.getUTCFullYear(), rangeStart.getUTCMonth(), rangeStart.getUTCDate(), 0, 0, 0, 0),
  );
  const endDay = new Date(
    Date.UTC(rangeEnd.getUTCFullYear(), rangeEnd.getUTCMonth(), rangeEnd.getUTCDate(), 23, 59, 59, 999),
  );

  while (currentDay <= endDay) {
    for (const slot of hoursRange) {
      if (isNowInSlot(now, slot.startTime, slot.endTime, currentDay)) {
        return true;
      }
    }
    // Move to next day
    currentDay = new Date(
      Date.UTC(currentDay.getUTCFullYear(), currentDay.getUTCMonth(), currentDay.getUTCDate() + 1, 0, 0, 0, 0),
    );
  }
  return false;
};

// Check if there are any upcoming slots in the future within the range
// Iterates through each day and checks if any slot start time is after now
const checkIfHasFutureSlots = (now: Date, hoursRange: PrizePopIntervalTimeRange[], rangeStart: Date, rangeEnd: Date) => {
  let currentDay = new Date(
    Date.UTC(rangeStart.getUTCFullYear(), rangeStart.getUTCMonth(), rangeStart.getUTCDate(), 0, 0, 0, 0),
  );
  const endDay = new Date(
    Date.UTC(rangeEnd.getUTCFullYear(), rangeEnd.getUTCMonth(), rangeEnd.getUTCDate(), 23, 59, 59, 999),
  );

  while (currentDay <= endDay) {
    for (const slot of hoursRange) {
      const slotStart = parseSlotTime(slot.startTime, currentDay);
      if (now < slotStart) {
        return true;
      }
    }
    // Move to next day
    currentDay = new Date(
      Date.UTC(currentDay.getUTCFullYear(), currentDay.getUTCMonth(), currentDay.getUTCDate() + 1, 0, 0, 0, 0),
    );
  }
  return false;
};

// Check if there are any past or active slots (i.e., campaign has started)
// Iterates through each day and checks if any slot has already begun
const checkIfHasPastOrActiveSlots = (now: Date, hoursRange: PrizePopIntervalTimeRange[], rangeStart: Date, rangeEnd: Date) => {
  let currentDay = new Date(
    Date.UTC(rangeStart.getUTCFullYear(), rangeStart.getUTCMonth(), rangeStart.getUTCDate(), 0, 0, 0, 0),
  );
  const endDay = new Date(
    Date.UTC(rangeEnd.getUTCFullYear(), rangeEnd.getUTCMonth(), rangeEnd.getUTCDate(), 23, 59, 59, 999),
  );

  while (currentDay <= endDay) {
    for (const slot of hoursRange) {
      const slotStart = parseSlotTime(slot.startTime, currentDay);
      if (now >= slotStart) {
        return true;
      }
    }
    // Move to next day
    currentDay = new Date(
      Date.UTC(currentDay.getUTCFullYear(), currentDay.getUTCMonth(), currentDay.getUTCDate() + 1, 0, 0, 0, 0),
    );
  }
  return false;
};

/**
 * Evaluate campaign intervals to determine current status:
 * - hasIntervals: whether campaign has time intervals configured
 * - isActive: whether campaign is currently in an active time slot
 * - allIntervalsEnded: whether all intervals have finished
 * - isPromo: whether campaign is in promo state (no intervals have started yet)
 */
export const evaluateCampaignIntervals = (campaign: IPrizePopCampaign) => {
  if (!campaign?.intervals || campaign.intervals.length === 0) {
    const now = new Date();
    const campaignEndDate = campaign?.endDate ? new Date(campaign.endDate) : null;
    const isFinished = campaignEndDate ? now > campaignEndDate : false;
    return { hasIntervals: false, isActive: false, allIntervalsEnded: isFinished, isPromo: false };
  }

  const now = new Date();
  let isActive = false;
  let allIntervalsEnded = true;
  let hasStartedInterval = false;

  for (const interval of campaign.intervals) {
    const rangeStart = new Date(interval.range.startRangeDate);
    const rangeEnd = new Date(interval.range.endRangeDate);
    const hoursRange = interval.hoursRange || [];

    const intervalHasEnded = now > rangeEnd;

    // Skip intervals that have already ended
    if (intervalHasEnded) {
      hasStartedInterval = true;
      continue;
    }

    const intervalNotStarted = now < rangeStart;
    const intervalIsActive = now >= rangeStart && now <= rangeEnd;

    // Mark that not all intervals have ended if we find a future one
    if (intervalNotStarted) {
      allIntervalsEnded = false;
      continue;
    }

    // Process currently active interval
    if (intervalIsActive) {
      if (hoursRange.length === 0) {
        hasStartedInterval = true;
        allIntervalsEnded = false;
        continue;
      }

      const slotIsActive = checkIfSlotIsActive(now, hoursRange, rangeStart, rangeEnd);
      const hasFutureSlots = checkIfHasFutureSlots(now, hoursRange, rangeStart, rangeEnd);
      const hasPastOrActiveSlots = checkIfHasPastOrActiveSlots(now, hoursRange, rangeStart, rangeEnd);

      // Mark as started only if at least one time slot has begun
      if (hasPastOrActiveSlots) {
        hasStartedInterval = true;
      }

      // If we found an active slot, campaign is active right now
      if (slotIsActive) {
        isActive = true;
        allIntervalsEnded = false;
        break;
      }

      // If there are future slots, campaign hasn't ended yet
      if (hasFutureSlots) {
        allIntervalsEnded = false;
      }
    }
  }

  // Campaign is in promo state if no interval has started yet
  const isPromo = !hasStartedInterval;

  return { hasIntervals: true, isActive, allIntervalsEnded, isPromo };
};

/**
 * PrizePop campaign status, derived from interval-based evaluation.
 * Matches the shared { isActive, isFinished, isPromo } shape used by useCampaignStatus.
 */
export const evaluatePrizePopCampaignStatus = (campaign: CampaignSource) => {
  const { hasIntervals, isActive, allIntervalsEnded, isPromo } = evaluateCampaignIntervals(campaign as IPrizePopCampaign);
  if (!hasIntervals) return { isActive: false, isFinished: false, isPromo: false };
  return { isActive, isFinished: allIntervalsEnded, isPromo };
};
